/**
 * Document view: read the PDF, consent, sign.
 */
import {
  ApiError,
  base64ToBlob,
  downloadBlob,
  getEnvelope,
  getEnvelopeFile,
  messageOf,
  signEnvelope,
} from '../api';
import type { EnvelopeDetail, Signer } from '../api';
import { announce, el, formatDate, joinNames, messageSlot, onClickAsync, setBusy, setDocumentTitle } from '../ui';
import { envelopeStatusPill, isMe, outstandingSigners, signerRoster } from './roster';
import { createSignaturePanel } from './signature-panel';
import type { SignaturePanel, SignatureStatus } from './signature-panel';
import type { Teardown, ViewContext } from './view';

/** What just happened, so the view can say something specific about it. */
type JustSigned = 'none' | 'recorded' | 'completed';

/** Ties the Submit button to the line that explains why it is disabled. */
const SIGN_STATUS_ID = 'sign-status';

/** Plain language for what is still outstanding before this can be signed. */
function describeStatus(state: SignatureStatus): string {
  if (state.ready) return 'Ready to sign.';
  const missing: string[] = [];
  if (state.needsConsent) missing.push('check the consent box');
  if (state.needsSignature) {
    missing.push(state.mode === 'draw' ? 'draw your signature above' : 'type your full legal name');
  }
  return `Before you can sign: ${joinNames(missing)}.`;
}

export function mountEnvelope(host: HTMLElement, ctx: ViewContext, id: string): Teardown {
  setDocumentTitle('Document');

  const myEmail = ctx.user?.email ?? '';
  let disposed = false;
  let objectUrl: string | null = null;
  let panel: SignaturePanel | null = null;
  let justSigned: JustSigned = 'none';

  function releaseObjectUrl(): void {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
  }

  function destroyPanel(): void {
    panel?.destroy();
    panel = null;
  }

  host.replaceChildren(el('p', { class: 'muted', text: 'Loading the document...' }));

  /* ---------------------------------------------------------------------- */
  /* Rendering                                                              */
  /* ---------------------------------------------------------------------- */

  function backButton(): HTMLElement {
    return el('button', {
      class: 'back-link',
      text: 'All documents',
      attrs: { type: 'button' },
      on: { click: () => ctx.navigate('/') },
    });
  }

  function reader(fileUrl: string, blob: Blob, filename: string): HTMLElement {
    const download = el('button', {
      class: 'button button-quiet button-small',
      text: 'Download a copy',
      attrs: { type: 'button' },
      on: { click: () => downloadBlob(blob, filename) },
    });

    return el('section', {
      class: 'reader',
      children: [
        el('div', {
          class: 'reader-bar',
          children: [
            el('span', { class: 'reader-name', text: filename }),
            el('div', {
              class: 'reader-tools',
              children: [
                el('a', {
                  class: 'button button-quiet button-small',
                  text: 'Open in new tab',
                  attrs: { href: fileUrl, target: '_blank', rel: 'noopener' },
                }),
                download,
              ],
            }),
          ],
        }),
        el('iframe', {
          class: 'reader-frame',
          attrs: { src: fileUrl, title: `${filename} - document preview` },
        }),
        el('p', {
          class: 'fine-print',
          text: 'If the document does not appear above, open it in a new tab or download a copy.',
        }),
      ],
    });
  }

  function completedPanel(detail: EnvelopeDetail): HTMLElement {
    const completedOn = formatDate(detail.envelope.completedAt);
    const messages = messageSlot();
    const download = el('button', {
      class: 'button button-primary',
      text: 'Download the signed PDF',
      attrs: { type: 'button' },
    });

    onClickAsync(download, 'Preparing...', async () => {
      messages.clear();
      try {
        const file = await getEnvelopeFile(id, true);
        downloadBlob(base64ToBlob(file.base64, file.contentType), file.filename);
        announce(`Downloading ${file.filename}.`);
      } catch (err) {
        messages.show('error', messageOf(err, 'We could not prepare that download.'));
      }
    });

    return el('section', {
      class: 'card panel-done',
      children: [
        el('h2', { class: 'panel-title', text: 'This document is fully executed' }),
        el('p', {
          class: 'lede',
          text: completedOn
            ? `Everyone signed. Completed ${completedOn}.`
            : 'Everyone has signed. The executed copy includes a certificate of electronic signature.',
        }),
        messages.node,
        download,
      ],
    });
  }

  function waitingPanel(signers: Signer[], mine: Signer | undefined): HTMLElement {
    const outstanding = outstandingSigners(signers).map((signer) =>
      isMe(signer, myEmail) ? 'you' : signer.name,
    );
    const signedOn = formatDate(mine?.signedAt ?? null);

    const heading =
      justSigned === 'recorded'
        ? 'Your signature is recorded'
        : mine && mine.status === 'signed'
          ? 'You have signed this document'
          : 'This document is not ready for you yet';

    const detail =
      outstanding.length > 0
        ? `Still waiting on ${joinNames(outstanding)}. You will get an email when everyone has signed.`
        : 'All signatures are in. The executed copy will be ready shortly.';

    return el('section', {
      class: 'card panel-waiting',
      children: [
        el('h2', { class: 'panel-title', text: heading }),
        signedOn ? el('p', { class: 'lede', text: `You signed ${signedOn}.` }) : false,
        el('p', { class: 'lede', text: detail }),
      ],
    });
  }

  function signingPanel(detail: EnvelopeDetail): HTMLElement {
    const messages = messageSlot();
    const submit = el('button', {
      class: 'button button-primary button-block',
      text: 'Sign this document',
      attrs: { type: 'button', 'aria-describedby': SIGN_STATUS_ID },
    });
    submit.disabled = true;

    // A disabled button never fires a click, so the reason it is disabled has to
    // be on the page at all times rather than waiting for someone to press it.
    const statusLine = el('p', {
      class: 'sign-status',
      attrs: { id: SIGN_STATUS_ID, role: 'status' },
    });

    function applyStatus(state: SignatureStatus): void {
      submit.disabled = !state.ready;
      statusLine.textContent = describeStatus(state);
      statusLine.classList.toggle('sign-status-ready', state.ready);
    }

    destroyPanel();
    panel = createSignaturePanel({
      suggestedName: detail.signers.find((signer) => isMe(signer, myEmail))?.name ?? ctx.user?.name ?? '',
      onValidityChange: applyStatus,
    });
    applyStatus(panel.status());

    let submitting = false;
    submit.addEventListener('click', () => {
      if (submitting || submit.disabled) return;
      const current = panel;
      if (!current) return;

      const request = current.value();
      if (!request) {
        // Readiness is already enforced by the disabled state, so the only way
        // here is a signature the pad could not turn into a usable image.
        messages.show('error', 'We could not prepare that signature. Clear it and draw again, or use the Type tab.');
        return;
      }

      submitting = true;
      messages.clear();
      setBusy(submit, true, 'Signing...');

      void signEnvelope(id, request)
        .then((result) => {
          if (disposed) return;
          justSigned = result.completed ? 'completed' : 'recorded';
          announce(
            result.completed
              ? 'Signed. This document is now fully executed.'
              : 'Signed. Your signature has been recorded.',
          );
          return load();
        })
        .catch((err: unknown) => {
          if (disposed) return;
          if (err instanceof ApiError && err.status === 409) {
            // Already signed elsewhere - the truth is on the server, so reload.
            justSigned = 'recorded';
            return load();
          }
          messages.show('error', messageOf(err, 'We could not record your signature. Please try again.'));
          return undefined;
        })
        .finally(() => {
          submitting = false;
          if (!disposed && submit.isConnected) {
            setBusy(submit, false);
            const live = panel;
            if (live) applyStatus(live.status());
            else submit.disabled = true;
          }
        });
    });

    return el('section', {
      class: 'card panel-sign',
      children: [
        el('h2', { class: 'panel-title', text: 'Sign this document' }),
        el('p', {
          class: 'lede',
          text: 'Read the document above. When you are ready, consent and add your signature.',
        }),
        panel.node,
        messages.node,
        submit,
        statusLine,
      ],
    });
  }

  function render(detail: EnvelopeDetail, fileUrl: string, blob: Blob, filename: string): void {
    const { envelope, signers } = detail;
    const mine = signers.find((signer) => isMe(signer, myEmail));
    const canSign =
      mine !== undefined &&
      mine.status !== 'signed' &&
      envelope.status !== 'completed' &&
      envelope.status !== 'voided';

    setDocumentTitle(envelope.title);

    const banner =
      justSigned === 'completed'
        ? el('p', {
            class: 'notice notice-success',
            text: 'Signed. This document is now fully executed.',
            attrs: { role: 'status' },
          })
        : justSigned === 'recorded'
          ? el('p', {
              class: 'notice notice-success',
              text: 'Signed. Your signature has been recorded.',
              attrs: { role: 'status' },
            })
          : null;

    const action = canSign
      ? signingPanel(detail)
      : envelope.status === 'completed'
        ? completedPanel(detail)
        : envelope.status === 'voided'
          ? el('section', {
              class: 'card panel-waiting',
              children: [
                el('h2', { class: 'panel-title', text: 'This document was voided' }),
                el('p', { class: 'lede', text: 'It can no longer be signed. Contact the sender for details.' }),
              ],
            })
          : waitingPanel(signers, mine);

    if (!canSign) destroyPanel();

    host.replaceChildren(
      backButton(),
      el('div', {
        class: 'doc-head doc-head-single',
        children: [
          el('h1', { class: 'title', text: envelope.title }),
          envelopeStatusPill(envelope.status),
        ],
      }),
      banner ?? el('span', { class: 'sr-only' }),
      reader(fileUrl, blob, filename),
      el('section', { class: 'card', children: [signerRoster(signers, myEmail)] }),
      action,
    );
  }

  /* ---------------------------------------------------------------------- */
  /* Loading                                                                */
  /* ---------------------------------------------------------------------- */

  async function load(): Promise<void> {
    try {
      const detail = await getEnvelope(id);
      if (disposed) return;

      // Once executed, the copy worth reading is the one with the signatures.
      const file = await getEnvelopeFile(id, detail.envelope.status === 'completed');
      if (disposed) return;

      const blob = base64ToBlob(file.base64, file.contentType);
      releaseObjectUrl();
      objectUrl = URL.createObjectURL(blob);
      render(detail, objectUrl, blob, file.filename);
    } catch (err) {
      if (disposed) return;
      if (err instanceof ApiError && err.isAuthError) {
        ctx.refresh();
        return;
      }
      const messages = messageSlot();
      host.replaceChildren(
        backButton(),
        el('section', {
          class: 'card card-narrow',
          children: [
            el('h1', { class: 'title', text: 'We could not open this document' }),
            messages.node,
            el('button', {
              class: 'button button-primary',
              text: 'Try again',
              attrs: { type: 'button' },
              on: { click: () => void load() },
            }),
          ],
        }),
      );
      messages.show(
        'error',
        err instanceof ApiError && err.status === 403
          ? 'This document is not shared with your email address. Ask the sender to invite you.'
          : messageOf(err, 'Something went wrong opening this document.'),
      );
    }
  }

  void load();

  return () => {
    disposed = true;
    destroyPanel();
    releaseObjectUrl();
  };
}
